import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ColumnDefinition, DynamicTableData } from '../../models/dynamic-table/dynamic-table-data.model';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AppMainService } from 'src/app/app-main.service';
import { MessageService } from 'primeng/api';
import { DynamicTableFormService } from '../../services/dynamic-table/dynamic-table-form.service';
import { Subject, takeUntil } from 'rxjs';
import { FormControlListIds } from '../../constants/formcontrol-list.constants';
import { OverlayPanel } from 'primeng/overlaypanel';
import { countPlainTextLength } from '../../helpers/count-plaintext-length';

@Component({
  selector: 'app-dynamic-workbook-form',
  templateUrl: './dynamic-workbook-form.component.html',
  styleUrl: './dynamic-workbook-form.component.scss'
})
export class DynamicWorkbookFormComponent {
  @Input() columnDefinitions: ColumnDefinition[];
  @Input() displayModal: boolean;
  @Input() dynamicTableData: DynamicTableData;
  @Input() formID?: string; // Optional FormID input

  @Output() closeModalEvent = new EventEmitter<void>();
  @Output() createdFormEvent = new EventEmitter<boolean>();
  @ViewChild('overlayPanel') overlayPanel: OverlayPanel;

  /** Subscription and Cleanup */
  protected ngUnsubscribe$: Subject<void> = new Subject<void>();
  isEditHeader = true;
  formHeader: string; // To store the header value
  updatedColumnDefinitions: any[];
  form: FormGroup;
  isSubmitted: boolean = false;

  isShare = false;
  isLinkSetting: boolean = false
  formPassword: string = '';
  passwordFieldType: string = 'password';  // Default type for password field
  shareLink: string;
  iframeCode :string;

  linkSettings = [
    { name: "Anyone with the link", key: "anyone" },
    { name: "Restrict access with a password", key: "password" },
    // {name:"Restrict access with login",key:"login"},
  ]
  selectedLinkSetting;
  formNameError='';
  checkedSingleLine = false;
  // displayAddDescription = false;
  tempDescription = [
    {id:1,description:""},
    {id:2,description:""},
    {id:3,description:""},
  ]
  description = [
    {id:1,description:""},
    {id:2,description:""},
    {id:3,description:""},
  ]

  themes = [
    { name: "green", color: "#9fd037" },
    { name: "teal", color: "#12b886" },
    { name: "blue", color: "#3ebaf8" },
    { name: "amber", color: "#f7cb00" },
    { name: "purple", color: "#966af1" },
    { name: "turquoise", color: "#2ab1be" },
    { name: "bluegrey", color: "#546E7A" },
    { name: "red", color: "#CE1800" },
  ];

  selectedTheme = this.themes.find(theme => theme.name === 'teal'); // Default theme
  descriptionText;
  dropdownVisible = false; // Used to toggle dropdown visibility
  countPlainTextLength=countPlainTextLength;

  constructor(
    private readonly fb: FormBuilder,
    public dynamicTableFormService: DynamicTableFormService,
    private readonly appMainService: AppMainService,
    private readonly messageService: MessageService
  ) { }

  ngOnInit(): void {
    // this.updatedColumnDefinitions = this.columnDefinitions.map(col => ({ ...col, selected: true })); // Add selected flag
    this.updatedColumnDefinitions = this.columnDefinitions
  .filter(field => ![FormControlListIds.AutoNumber, FormControlListIds.Formula, FormControlListIds.UserLookup]
    .includes(field.formControlId))
  .map(col => ({ ...col, selected: true }));
    // this.formHeader = `${this.dynamicTableData.name} Form`;
    this.buildForm();
    if (this.formID) {
      this.getExistingForm(this.formID);
      this.isShare = true;
      this.isEditHeader = false;
      this.prepareSharableLink()
    }
  }

  selectTheme(theme: any) {
    this.selectedTheme = theme;
    this.overlayPanel.hide();  // Close the overlay after selection
  }

  prepareSharableLink() {
    const baseUrl = window.location.origin;
    this.shareLink = `${baseUrl}/form/${this.dynamicTableData.id}/${this.formID}`;
    this.iframeCode = `
    <iframe 
      src="${this.shareLink}" 
      width="100%" 
      height="100%" 
      style="border: none; overflow: hidden; display: block;" 
      loading="lazy" 
      allowfullscreen>
    </iframe>`;
  }
  getExistingForm(formID: string) {
    this.appMainService.isShowSpinner('getExistingForm');
    this.dynamicTableFormService.getDynamicForm(formID).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('getExistingForm');
        if (response?.authenticationId === 1) {
          this.selectedLinkSetting = this.linkSettings[1];
        }
        else this.selectedLinkSetting = this.linkSettings[0];
        this.patchForm(response); // Patch the form with retrieved data
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getExistingForm');
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to load form data!' });
      }
    });
  }

  patchForm(formData: any) {
    this.formHeader = formData.name; 
    this.descriptionText = formData.description;

    // Update `selected` flag based on the existing form data
    this.updatedColumnDefinitions = this.columnDefinitions.filter(field => ![FormControlListIds.AutoNumber, FormControlListIds.Formula, FormControlListIds.UserLookup]
      .includes(field.formControlId)).map(col => {
      // Check if the current column exists in the formData.columns
      const matchingColumn = formData.columns.find((existingCol: any) => existingCol.id === col.id);

      return {
        ...col,
        selected: !!matchingColumn, // Set selected to true if it exists, false otherwise
        isRequired: matchingColumn?.isRequired ?? false
      };
    });
    // Patch only the checkbox controls without affecting the input controls
    this.updatedColumnDefinitions.forEach(column => {
      const checkboxControl = this.form.get(`checkbox_${column.id}`);
      if (checkboxControl) {
        checkboxControl.setValue(column.selected); 
      }
      const isRequiredControl = this.form.get(`isRequired_${column.id}`);
      if (isRequiredControl) {
        isRequiredControl.setValue(column.isRequired); 
      }
    });
  }

  buildForm() {
    const group: { [key: string]: FormControl } = {};
    group["selectAll"] = new FormControl(false);
    group["selectAllRequired"] = new FormControl(false);
    this.updatedColumnDefinitions.forEach(column => {
      // Checkbox control (true means selected, false means deselected)
      group[`checkbox_${column.id}`] = new FormControl(true); // Initially, all checkboxes are checked
      // Input control, initially disabled
      group[column?.id] = new FormControl({ value: null, disabled: true });
      group[`isRequired_${column.id}`] = new FormControl(true);
    });
    this.form = this.fb.group(group,{
      validators: [this.atLeastOneCheckboxValidator()] // Add the custom validator
    });

    this.form.get("selectAll")?.valueChanges.subscribe((selectAllChecked: boolean) => {
      this.updatedColumnDefinitions.forEach(column => {
        this.form.get(`checkbox_${column.id}`)?.setValue(selectAllChecked);
        if (!selectAllChecked) {
          this.form.get(`isRequired_${column.id}`)?.setValue(false);
        }
      });
      if (!selectAllChecked) {
        this.form.get("selectAllRequired")?.setValue(false);
      }
    });

    this.form.get("selectAllRequired")?.valueChanges.subscribe((selectAllRequired: boolean) => {
      this.updatedColumnDefinitions.forEach(column => {
        if (this.form.get(`checkbox_${column.id}`)?.value) { 
          // Only update required field if the field is selected
          this.form.get(`isRequired_${column.id}`)?.setValue(selectAllRequired);
        }
      });
  });
  
    // Listen to checkbox changes to enable/disable the input controls
    this.updatedColumnDefinitions.forEach(column => {
      this.form.get(`checkbox_${column.id}`)?.valueChanges.subscribe((checked: boolean) => {
        const inputControl = this.form.get(column?.id);
        inputControl?.disable(); // Disable input when checkbox is unchecked
      });
    });
  }

    // Custom validator to check that at least one checkbox is checked
    atLeastOneCheckboxValidator(): ValidatorFn {
      return (formGroup: FormGroup): ValidationErrors | null => {
        const controls = formGroup.controls;
        const atLeastOneSelected = Object.keys(controls).some(key => key.startsWith('checkbox_') && controls[key].value);

        const atLeastOneRequired = Object.keys(controls).some(
          key => key.startsWith('isRequired_') && controls[key].value
        );
        const errors: ValidationErrors = {};
    
        if (!atLeastOneSelected) {
          errors['atLeastOneSelected'] = true;
        }
        
        if (!atLeastOneRequired) {
          errors['atLeastOneRequired'] = true;
        }
        
        return Object.keys(errors).length > 0 ? errors : null;
      
      };
    }

  // Handle drag and drop to update field order
  drop(event: CdkDragDrop<ColumnDefinition[]>) {
    moveItemInArray(this.updatedColumnDefinitions, event.previousIndex, event.currentIndex);
  }

  // Handle delete field
  deleteField(columnId: string) {
    this.updatedColumnDefinitions = this.updatedColumnDefinitions.filter(col => col.id !== columnId);
    this.form.removeControl(columnId); // Remove from the form
  }
  saveForm() {
    if(this.validateFormName()){
      if (this.form.invalid) {
        // If the form fails the custom validator, display an error message
        if (this.form.errors?.atLeastOneSelected) {
          this.messageService.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: 'At least one field must be selected.'
          });
        } else if (this.form.errors?.atLeastOneRequired) {
          this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'At least one field must be required.' });
        }
        return;
      }
      this.isSubmitted = true;
      const result = this.updatedColumnDefinitions
        .filter(col => this.form.get(`checkbox_${col.id}`)?.value) // Only include selected columns
        .map((col, index) => ({
          id: col.id,
          order: index + 1 , // Update order based on array index
          isRequired: this.form.get(`isRequired_${col.id}`)?.value ?? false,
        }));
  
      const newDynamicForm: any = {
        name: this.formHeader,
        tableId: this.dynamicTableData.id,
        authenticationId: 0,
        columns: result,
        password: "",
        description: this.descriptionText
      };
      this.createDynamicForm(newDynamicForm)
    }
  }
  updateForm() {
    if(this.validateFormName()){
      if (this.form.invalid) {
        // If the form fails the custom validator, display an error message
        if (this.form.errors?.atLeastOneRequired) {
          this.messageService.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: 'At least one field must be selected.'
          });
        }
        return;
      }
      this.isSubmitted = true;
      const result = this.updatedColumnDefinitions
        .filter(col => this.form.get(`checkbox_${col.id}`)?.value) // Only include selected columns
        .map((col, index) => ({
          dynamicColumnId: col.id,
          order: index + 1 ,// Update order based on array index
          isRequired: this.form.get(`isRequired_${col.id}`)?.value ?? false,
        }));
  
      const newDynamicForm: any = {
        tableId: this.dynamicTableData.id,
        name: this.formHeader,
        columns: result,
        description: this.descriptionText
      };
      if (this.formID) {
        this.updateDynamicForm(newDynamicForm);
      }
    }
  }
  resetAuthenticationState() {
    this.formPassword = "";
    this.isLinkSetting = false
  }

  createDynamicForm(dynamicForm: any) {
    this.appMainService.isShowSpinner('createDynamicForm');
    this.dynamicTableFormService.createDynamicForm(dynamicForm).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(
      {
        next: (response: any) => {
          this.isSubmitted = false;
          this.appMainService.isHideSpinner('createDynamicForm');
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Form is created!' });
          this.formID = response
          this.isShare = true;
          this.selectedLinkSetting = this.linkSettings[0];
          this.prepareSharableLink();
          this.createdFormEvent.emit(true);
        },
        error: (error) => {
          this.isSubmitted = false;
          this.appMainService.isHideSpinner('createDynamicForm');
          this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to create Form!' });

        }
      },
    );
  }

  updateDynamicForm(dynamicForm: any) {
    this.appMainService.isShowSpinner('updateDynamicForm');
    this.dynamicTableFormService.editDynamicForm(this.formID, dynamicForm).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(
      {
        next: (response: any) => {
          this.isSubmitted = false;
          this.appMainService.isHideSpinner('updateDynamicForm');
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Form is Updated!' });
          this.isShare = true;
          this.createdFormEvent.emit(true);

        },
        error: (error) => {
          this.isSubmitted = false;
          this.appMainService.isHideSpinner('updateDynamicForm');
          this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to update Form!' });

        }
      },
    );
  }

  updateAuthentication(): void {
    this.isSubmitted = true;
    let updateAuthPayload: { authenticationId: number; password: string };
  
    if (this.selectedLinkSetting?.key === 'anyone') {
      this.formPassword = ""; // No password if the setting is 'anyone'
      updateAuthPayload = {
        authenticationId: 0,
        password: this.formPassword
      };
    } else if (this.selectedLinkSetting?.key === 'password') {
      updateAuthPayload = {
        authenticationId: 1,
        password: this.formPassword
      };
    }
  
    // Call the API to update the form's authentication
    this.dynamicTableFormService.updateFormAuthentication(this.formID, updateAuthPayload).subscribe({
      next: (response) => {
        this.isSubmitted = false;
        this.resetAuthenticationState();  // Reset the state
        this.getExistingForm(this.formID);  // Fetch the updated form data
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Password updated!' });
      },
      error: (error) => {
        this.isSubmitted = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to update password!' });
      }
    });
  }
  
  validateFormName() {
    const specialCharRegex = /^[a-zA-Z0-9*@()-_#$ ]*$/; 
  
    // Check if the Form name is blank or only has spaces
    if (!this.formHeader || this.formHeader.trim().length === 0) {
      this.formNameError = 'Form name cannot be blank or contain only spaces.';
      return false;
    }
  
    // Check if Form name starts with a space
    if (this.formHeader.startsWith(' ')) {
      this.formNameError = 'Form name cannot start with a space.';
      return false;
    }
  
    // Check if the length exceeds 30 characters
    if (this.formHeader.length > 30) {
      this.formNameError = 'Form name should be less than 30 characters.';
      return false;
    }
  
    // Check if it contains invalid special characters
    if (!specialCharRegex.test(this.formHeader)) {
      this.formNameError = 'Form name contains invalid characters. Only @,-, _, #, $, (, ) are allowed.';
      return false;
    }
  
    // If everything is valid
    this.formNameError = '';
    this.isEditHeader = false;
    return true;
  }

  // Handle close modal
  closeModal() {
    this.closeModalEvent.emit();
  }

  copyLink(): void {
    navigator.clipboard.writeText(this.shareLink).then(() => {
      this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Link copied to clipboard' });
    });
  }
  
  copyEmbedCode(): void {
    navigator.clipboard.writeText(this.iframeCode).then(() => {
      this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Embed code copied to clipboard'});
    });
  }

  togglePasswordVisibility() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  validateNoSpaces(passwordInput: any) {
    const value = passwordInput.value || '';
    
    // Check for spaces in the password
    if (/\s/.test(value)) {
      passwordInput.control.setErrors({ noSpaces: true });
    } else {
      // Clear the noSpaces error if no spaces are found
      if (passwordInput.control.hasError('noSpaces')) {
        passwordInput.control.setErrors(null);
      }
    }
  }

  // addDescription(){
  //   this.displayAddDescription = true;
  // }
  // saveDescription(){
  //   this.description = JSON.parse(JSON.stringify(this.tempDescription));
  //   this.displayAddDescription = false;
  // }

  onCheckboxChange(columnId: string) {
    const mainCheckboxControl = this.form.get('checkbox_' + columnId);
    const requiredCheckboxControl = this.form.get('isRequired_' + columnId);
  
    if (mainCheckboxControl?.value) {
      requiredCheckboxControl?.enable();
    } else {
      requiredCheckboxControl?.setValue(false); // Uncheck "Required" if unchecked
      requiredCheckboxControl?.disable();
    }
    
  }
  
  isCheckboxChecked(columnId: string): boolean {
    return this.form.get('checkbox_' + columnId)?.value;
  }

  isRequiredChecked(columnId: string): boolean {
    return this.form.get('isRequired_' + columnId)?.value;
  }
  
  

}
